import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, ChevronsUpDown } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  applyRouteChange,
  filterNavForRole,
  initialOpenGroups,
  isNavItem,
  loadOpenGroups,
  resolveActiveGroup,
  saveOpenGroups,
  toggleGroup,
} from '../../lib/sidebarNav';
import { firebaseAuth } from '../../lib/firebase';
import { cn } from '../../lib/utils';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import { useCurrentUser } from '../../modules/auth';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { KyckLogo } from '../branding/KyckLogo';
import { avatarInitials } from './avatarInitials';
import {
  DASHBOARD_ITEM,
  NAV_SECTIONS,
  SETTINGS_ITEM,
  type NavItemDef,
} from './navStructure';

// Sidebar — Design System Phase 3 §1/§2. Full-height, 240px, light slate-50
// surface with grouped navigation. Active-route highlighting uses NavLink's
// isActive (exact match only where `end` is set, so detail routes keep their
// section's parent item highlighted).
//
// COLLAPSIBLE GROUPS (2026-07-19): the nav had grown to ~18 items across 6
// groups — more than anyone can scan. Groups now collapse on header click, the
// open set persists (localStorage), and the group containing the current route
// auto-expands on navigation so you never lose your place. With nothing stored,
// only the current route's group is open.
//
// RESTRUCTURE (2026-09-17): 32 items across 9 groups, regrouped by job. WHAT IS
// IN THE MENU now lives in ./navStructure.ts — this file renders it. Two levels
// became three: a group may carry SUB-HEADERS, which are labels rather than
// collapsible groups (see sidebarNav.ts for why that keeps the persistence layer
// untouched). Dashboard joined Settings as a pinned item outside the groups.
//
// DECISION — extended this hand-rolled Sidebar rather than adopting shadcn's
// `Sidebar` component. That component brings a provider + rail + offcanvas +
// mobile-sheet system and a `@radix-ui/react-collapsible` dependency we don't
// have, and would need AppShell restructured — where the opt-in full-height
// mode is load-bearing for Community and Publishing. We needed "groups
// collapse", which is a button and a conditional render. Revisit if we ever
// want the icon-rail / mobile-offcanvas behaviour, which is what that component
// actually buys.
//
// DECISION — no icon-rail collapse (whole sidebar → icons). It saves horizontal
// space but not cognitive load, which was the actual complaint; noted as a
// later option.

function NavItem({ to, label, icon: Icon, end }: NavItemDef) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'my-0.5 flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kyck-500 focus-visible:ring-offset-1',
          isActive
            ? 'bg-kyck-50 font-semibold text-slate-900'
            : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={cn('h-4 w-4 shrink-0', isActive ? 'text-kyck-600' : 'text-slate-500')}
          />
          {label}
        </>
      )}
    </NavLink>
  );
}

/**
 * A heading inside an open group.
 *
 * ⚠️ Deliberately a plain element: no button, no link, no tabindex, no role. It
 * labels the items beneath it and does nothing else — making it interactive to
 * get a click target is the shortcut this design rejects, because it would add a
 * tab stop per sub-header for a section that cannot be navigated to.
 *
 * ⚠️ It reads as subordinate through CASE, not through being fainter. The first
 * cut used slate-400, measured at 2.51:1 against the rail on 11px text — well
 * under the 4.5:1 floor. slate-500 is the same ink as the group header above it;
 * what separates the two levels is sentence case against uppercase, plus the
 * missing chevron and the smaller size.
 */
function SubHeader({ label }: { label: string }) {
  return (
    <div className="px-3 pb-0.5 pt-3 text-[11px] font-semibold text-slate-500">{label}</div>
  );
}

function SectionHeader({
  label,
  open,
  onToggle,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center gap-1 rounded-md px-3 pb-1 pt-2 text-xs font-medium uppercase tracking-wider text-slate-500 transition-colors hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kyck-500"
    >
      <ChevronRight
        className={cn('h-3 w-3 shrink-0 transition-transform', open && 'rotate-90')}
        aria-hidden
      />
      {label}
    </button>
  );
}

export function Sidebar() {
  const user = useCurrentUser();
  const isAdmin = user.role === 'admin';
  const { pathname } = useLocation();

  // Admin-only sections, sub-headers and items are dropped — along with
  // anything they leave empty, so an AM never meets a bare "Operational"
  // header or a sub-header with nothing under it.
  const visibleSections = useMemo(() => filterNavForRole(NAV_SECTIONS, isAdmin), [isAdmin]);

  const activeGroup = useMemo(
    () => resolveActiveGroup(visibleSections, pathname),
    [visibleSections, pathname],
  );

  const [openGroups, setOpenGroups] = useState<string[]>(() =>
    initialOpenGroups(
      loadOpenGroups(visibleSections.map((s) => s.label)),
      resolveActiveGroup(visibleSections, pathname),
    ),
  );

  // Auto-expand the group you navigate INTO. Keyed on activeGroup (not every
  // render) so collapsing the section you're currently in stays collapsed until
  // you navigate elsewhere — force-opening every render would make the current
  // group impossible to close.
  useEffect(() => {
    setOpenGroups((cur) => applyRouteChange(cur, activeGroup));
  }, [activeGroup]);

  const onToggleGroup = (label: string) =>
    setOpenGroups((cur) => {
      const next = toggleGroup(cur, label);
      saveOpenGroups(next);
      return next;
    });

  return (
    <aside className="sticky top-0 flex h-screen w-60 flex-col border-r border-slate-200 bg-slate-50">
      {/* Logo area — 56px tall, dark-text wordmark on the light surface. */}
      <div className="flex h-14 items-center border-b border-slate-200 px-4">
        <KyckLogo variant="dark-text" size={32} />
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {/* Dashboard is pinned above the groups — it is where you land, not
            part of a job, so it belongs to no section. */}
        <NavItem {...DASHBOARD_ITEM} />
        <div className="my-3 border-t border-slate-200" />

        {visibleSections.map((section) => {
          const open = openGroups.includes(section.label);
          return (
            <div key={section.label} className={cn('last:mb-0', open ? 'mb-4' : 'mb-1')}>
              <SectionHeader
                label={section.label}
                open={open}
                onToggle={() => onToggleGroup(section.label)}
              />
              {open &&
                section.entries.map((entry) =>
                  isNavItem(entry) ? (
                    <NavItem key={entry.to} {...entry} />
                  ) : (
                    <SubHeader key={`sub:${entry.subheader}`} label={entry.subheader} />
                  ),
                )}
            </div>
          );
        })}

        {/* Settings divider + item */}
        <div className="my-4 border-t border-slate-200" />
        <NavItem {...SETTINGS_ITEM} />
      </nav>

      {/* User menu — sticky to the bottom, dropdown opens upward. */}
      <UserMenu />
    </aside>
  );
}

function UserMenu() {
  const user = useCurrentUser();
  const name = formatUserDisplayName(user, { full: true });
  const initials = avatarInitials(user.displayName, user.email);

  return (
    <div className="mt-auto border-t border-slate-200 p-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-md p-1.5 text-left transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kyck-500"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-kyck-100 text-xs font-semibold text-kyck-700">
              {initials}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-slate-900">{name}</span>
              <span className="block text-xs font-medium uppercase tracking-wide text-slate-500">
                {user.role}
              </span>
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start" className="w-[208px]">
          <DropdownMenuItem disabled>Profile</DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings">Account Settings</Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void signOut(firebaseAuth)}>
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
